
import { computed, inject, onErrorCaptured, ref } from 'vue';

import NodeHeader from '@/components/graph/common/NodeHeader.vue';
import DelaySelect from '@/components/common/DelaySelect.vue';
import { useStoreInNodes } from '@/composables/useStoreInNodes';
import NodeActions from '@/components/graph/common/NodeActions.vue';
import { getInnerText } from '@/utils';
import DataStorageSelect from '@/components/graph/common/DataStorageSelect.vue';
import { TOOLBAR_TOOLS } from '@/constants/amp';
import { onClickOutside } from '@/directives/onClickOutside';

export default {
  components: {
    DataStorageSelect,
    DelaySelect,
    NodeActions,
    NodeHeader,
  },

  directives: {
    'click-outside': onClickOutside,
  },

  layout: 'empty',

  props: {
    nodeId: { type: String, default: '' },
    subNodeId: { type: String, default: '' },
    nodeType: { type: String, default: '' },
    nodeActions: { type: Array, default: () => [] },
    nodeIndex: { type: Number, default: -1 },
    nodeTitle: { type: String, default: '' },
    ampData: { type: Object, default: () => ({}) },
    uiData: { type: Object, default: () => ({}) },
    isDone: { type: Boolean, default: false },
    isReadonly: { type: Boolean, default: false },
    isGroupNode: { type: Boolean, default: false },
    isSubNode: { type: Boolean, default: false },
    isBlurred: { type: Boolean, default: false },
    hideDelay: { type: Boolean, default: false },
    hideNodeHeader: { type: Boolean, default: false },
    hideActions: { type: Boolean, default: false },
    hideStorage: { type: Boolean, default: false },
    maxStorageSize: { type: Number, default: 0 },
  },

  setup(props, { emit }) {
    const { store } = useStoreInNodes();

    const getGraph = inject('getGraph', () => {});

    const showError = ref(false);

    const isHighlighted = computed(
      () => props.nodeId === store.value.state.builderStore?.highlightedNode && !props.subNodeId
    );
    const isHovered = computed(() => props.uiData.hovered);
    const isSelected = computed(() => props.uiData.selected);
    const isStartNode = computed(() => props.uiData.conversationStarter);

    const actions = computed(() =>
      isStartNode.value && !props.subNodeId ? [...props.nodeActions, { click: true, key: 'top' }] : props.nodeActions
    );

    const groupNodeTitle = computed(() =>
      props.isGroupNode ? TOOLBAR_TOOLS.find((tool) => tool.name === props.nodeType)?.nodeLabel || props.nodeType : ''
    );

    const nodeStyling = computed(() => {
      const classes = ['node'];

      if (props.isGroupNode) {
        classes.push('group-node');

        if (isStartNode.value) {
          classes.push('conversation-starter');
        }
      } else {
        if (props.isDone) {
          classes.push(isStartNode.value ? 'conversation-starter' : 'done-state');
        } else {
          classes.push('not-done-state');
        }

        if (isSelected.value) {
          classes.push('selected-node');
        }

        if (isHovered.value) {
          classes.push('hovered');
        }
      }

      if (isHighlighted.value) {
        classes.push('highlighted');
      }

      if (props.subNodeId) {
        classes.push('sub-node');
      }

      if (props.isReadonly) {
        classes.push('readonly');
      }

      return classes;
    });

    const htmlLength = computed(() =>
      props.ampData.html === undefined ? null : getInnerText(props.ampData.html).length
    );

    const onNodeClick = (event) => {
      emit('node-click', event);
    };

    const onDelayChange = (delay) => {
      emit('change-delay', delay);
    };

    const bringNodeToFront = () => {
      getGraph()?.getCellById(props.nodeId)?.toFront();
    };

    const onDrag = (event, eventName) => {
      event.dataTransfer.dropEffect = 'move';
      emit(eventName, event);
    };

    onErrorCaptured(() => {
      // TODO
    });

    return {
      actions,
      emit,
      htmlLength,
      isHighlighted,
      isHovered,
      isSelected,
      isStartNode,
      groupNodeTitle,
      nodeStyling,
      showError,
      bringNodeToFront,
      onNodeClick,
      onDelayChange,
      onDrag,
    };
  },
};
